// AIAssistant - the assistant launcher and chat panel.
//
// G360_K360 - the launcher renders INSIDE the pinned sidebar footer
// (passed to AppShell as sidebarFoot), not floating over the page. The
// panel still opens fixed at bottom-right: anchoring it to the sidebar
// would break on mobile, where the sidebar is hidden.
//
// This header said "bottom-left launcher" from before kit 129 moved it
// right, and stayed wrong through two position changes.

import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Bot, X, Send } from 'lucide-react';
import { askAI } from '../lib/aiApi.js';
// G360_K372 - grounded answers come from the metric registry; askAI above
// stays as the fallback for questions no metric answers.
import { useAuth, supabase } from '@govern360/shared'; // G360_K377 - supabase for gap capture
import { ask } from '../lib/metricAnswer.js';
import { METRIC_BY_KEY } from '../lib/metricRegistry.js';
/* G360_K852 - Govern360 Guide. Everything it needs was already on prod and
   inert; this import is what makes it exist, so until now there was nothing
   to see rather than something broken. */
import GuideRunner from './GuideRunner.jsx';
import deviceScopeGuide from '../lib/walkthrough/guides/device-scope.json';
import SurfaceGuide from './SurfaceGuide.jsx';
import { SURFACES } from '../lib/walkthrough/surfaces.generated.js';
import { matchSurfaceSmart } from '../lib/walkthrough/surface-match.js';

const GUIDES = [deviceScopeGuide];

/* G360_K853 - the catalogue of PLACES, as distinct from the catalogue of
   tasks above. An authored guide walks a change and reads the result back;
   this answers the far more common "where do I do X" for every surface at
   once, which is what stopped the Guide going silent on every question but
   one. */

/* A CATALOGUED guide is matched, never invented. No model call, for two
   reasons. Spec section 6: the assistant may only ever name a catalogued
   guide_id, so letting a model pick is a bigger surface than letting it not.
   Spec section 10: deterministic guidance must still work when the model is
   unavailable, and this does.

   A phrase matches when ALL of its words appear in the question, in any
   position - not as a contiguous substring. The first version required
   contiguity and failed on the canonical example from the design doc itself,
   "Help me exclude our lab devices from Guard coverage": the phrase is
   "exclude devices" and the question says "exclude OUR LAB devices". The most
   likely phrasing a user would type, missed.

   Phrases of a single word are ignored, because one generic word ("devices")
   would offer a walkthrough to anyone who mentioned it. Two words is the
   floor that keeps this specific.

   It still fails by offering NOTHING rather than the wrong walkthrough, which
   is the right direction to fail in: a missed offer is a question answered
   normally, a wrong offer is a confident instruction for the wrong task. */
const words = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(Boolean);

function matchGuide(question) {
  const q = new Set(words(question));
  if (!q.size) return null;
  for (const g of GUIDES) {
    for (const phrase of (g.intent || [])) {
      const pw = words(phrase);
      if (pw.length < 2) continue;
      if (pw.every((w) => q.has(w))) return g;
    }
  }
  return null;
}

/* G360_K509 - questions to start from. Each is the first example of a
   registered measure, so every one is answered from the same function the
   page uses - never a question the assistant would then refuse. A key
   that is not registered is skipped rather than offered. */
const STARTERS = ['exposure_findings', 'credential_status', 'orphaned_agents', 'exposure_legs', 'token_attribution', 'llm_top10']
  .map((k) => METRIC_BY_KEY[k] && METRIC_BY_KEY[k].examples && METRIC_BY_KEY[k].examples[0])
  .filter(Boolean);

export default function AIAssistant({ navGroups, onNavigate }) {
  const { tenant } = useAuth(); // G360_K372 - metrics run under this user's RLS
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [showRows, setShowRows] = useState(null); // G360_K372 - index of the answer showing its evidence
  // G360_K852 - the running guide, null when none. Started only by a click.
  const [guide, setGuide] = useState(null);
  // G360_K853 - the surface being shown, null when none. Also click-started.
  const [where, setWhere] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, busy]);

  /* G360_K853 - ROLE SCOPE, and it has to be done here rather than in the
     matcher. navGroups is ConsoleApp's VISIBLE_GROUPS, already filtered
     through routeAllowed(), so intersecting it with the registry yields
     exactly the pages THIS person may open. Matching over the whole
     registry would offer to walk someone into a page their role cannot
     open and, worse, disclose that it exists.
     External entries are dropped: implguide, adminguide and ainews call
     window.open() and set no hash, so there is no route to land on and
     nothing to point at. */
  const visibleSurfaces = useMemo(() => {
    const allowed = new Set();
    for (const g of navGroups || []) for (const it of g.items || []) allowed.add(it.id);
    return SURFACES.filter((s) => allowed.has(s.id) && !s.external);
  }, [navGroups]);

  async function send(asked) {
    // G360_K509 - a starter question is sent as typed text would be.
    const question = (typeof asked === 'string' ? asked : q).trim();
    if (!question || busy) return;
    setMsgs((m) => [...m, { role: 'user', text: question }]);
    setQ(''); setBusy(true);

    /* G360_K372 - a registered metric answers this, or it does not.
       When it does, the figure comes from the same function the page
       renders, so the assistant and the console cannot disagree. */
    /* G360_K377 - every refusal is a question somebody wanted answered
       and Govern360 could not. ask() passes route-stage refusals only,
       never transport failures, so an outage cannot fill the roadmap.

       Swallowed deliberately: a capture that breaks the answer it was
       observing is worse than no capture. */
    const onGap = (g) => {
      supabase.rpc('log_assistant_gap', {
        p_question: g.question, p_reason: g.reason, p_stage: g.stage,
      }).then(() => {}, () => {});
    };
    /* G360_K852 - does a catalogued walkthrough cover this? Computed here and
       attached to the reply as an OFFER. It is never started automatically:
       the assistant routes and explains, the human consents and clicks. */
    const offer = matchGuide(question);
    /* G360_K853 - an authored guide WINS. It walks the actual task and reads
       the result back from the server; a surface hop only opens the page.
       Offering both would make the weaker one look equivalent.

       G360_K854 - started BEFORE the answer and awaited after it, so the
       model-routing fallback overlaps the reply it travels with instead of
       being added to it. Routing a question the vocabulary missed should
       not make the assistant feel slower.
       matchSurfaceSmart tries the deterministic pass first and only calls
       the model on a miss, so the common case still costs nothing and
       guidance still works when no provider is configured. */
    const placePromise = offer
      ? Promise.resolve(null)
      : matchSurfaceSmart(question, visibleSurfaces, askAI);

    const res = await ask(question, tenant && tenant.id, onGap);
    if (!res.refused) {
      const place = await placePromise;
      setBusy(false);
      setMsgs((m) => [...m, { role: 'ai', text: res.text, metric: res.metric, rows: res.rows, guide: offer, place: place && { ...place, question } }]);
      return;
    }
    if (res.failed) {
      // The metric existed and could not be read. That is worth saying,
      // and it is not a question the conversational path can answer.
      setBusy(false);
      setMsgs((m) => [...m, { role: 'ai', text: res.text }]);
      return;
    }
    // Refused: no metric fits. Fall through - the product knowledge below
    // answers "what does autonomy tier mean" perfectly well.
    const { answer, error, needsProvider } = await askAI({ question, mode: 'assistant' });
    const place = await placePromise;
    setBusy(false);
    setMsgs((m) => [...m, { role: 'ai', text: error ? `Error: ${error}` : answer, needsProvider, guide: offer, place: place && { ...place, question } }]);
  }

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} aria-label="Open Ask Govern360"
          /* G360_K360 - inline, full width of the 248px sidebar footer.
             Kit 129 moved this to bottom-right because at left:16 a
             viewport-fixed button sat on top of the footer. Inside the
             footer that conflict cannot arise: it is no longer fixed. */
          style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            background: 'var(--g-primary)', color: '#fff', border: 0, borderRadius: 'var(--g-r-pill)', padding: '9px 14px',
            fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
          <Bot size={18} />Ask Govern360
        </button>
      )}
      {/* G360_K836 - PORTALLED TO document.body, and that is the fix, not the
          z-index. This component renders inside the sidebar footer
          (sidebarFoot), so although the panel is position:fixed it was still
          painted inside the sidebar's stacking context. Ordinary page buttons
          in a later context therefore drew straight through it - it looked
          transparent, but nothing was ever transparent: the background below
          is opaque var(--g-surface). z-index inside a trapped context cannot
          win, however high, so the panel has to leave the context. 900 keeps
          it above all page content while staying under the 1000 the modal
          backdrops use, because a modal must still cover it. */}
      {open && createPortal((
        <div style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 900, width: 380, maxWidth: 'calc(100vw - 48px)',
          background: 'var(--g-surface)', border: '1px solid var(--g-border)', borderRadius: 'var(--g-r-lg)', boxShadow: 'var(--g-e3)', display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--g-divider)', background: 'var(--g-primary)', color: '#fff', borderRadius: 'var(--g-r-lg) var(--g-r-lg) 0 0' }}>
            <Bot size={18} /><strong style={{ flex: 1, fontSize: 14 }}>Ask Govern360</strong>
            <button onClick={() => setOpen(false)} aria-label="Close" style={{ background: 'transparent', border: 0, color: '#fff', cursor: 'pointer' }}><X size={18} /></button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 120 }}>
            {msgs.length === 0 && <p style={{ color: 'var(--g-text-2)', fontSize: 13, margin: 0 }}>Ask about your exposure score and what to fix, agent and tool risk, credentials, orphaned agents, spend attribution, OWASP coverage or posture - or about how Govern360 itself works. Answers about your estate come from the same measures the pages use, and say when something is not measured.</p>}
            {msgs.length === 0 && STARTERS.length > 0 && (
              <div data-testid="assistant-starters" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {STARTERS.map((s) => (
                  <button key={s} type="button" onClick={() => send(s)} disabled={busy}
                    style={{ border: '1px solid var(--g-border)', background: 'var(--g-bg-soft)', color: 'var(--g-text)', borderRadius: 'var(--g-r-pill)',
                      padding: '5px 10px', fontSize: 12, cursor: 'pointer', textAlign: 'left' }}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}?
                  </button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%',
                background: m.role === 'user' ? 'var(--g-primary-50)' : 'var(--g-bg-soft)', borderRadius: 10, padding: '8px 12px', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                {m.text}
                {/* G360_K372 - a number a reader cannot trace is a number
                    they act on once and then stop trusting. */}
                {m.metric && (
                  <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--g-divider)', fontSize: 11, color: 'var(--g-text-2)' }}>
                    From {m.metric.title}
                    <button type="button" onClick={() => setShowRows(showRows === i ? null : i)}
                      style={{ marginLeft: 8, border: 0, background: 'none', padding: 0, font: 'inherit', color: 'var(--g-primary)', cursor: 'pointer', textDecoration: 'underline' }}>
                      {showRows === i ? 'hide evidence' : 'show evidence'}
                    </button>
                    {showRows === i && (
                      <pre style={{ marginTop: 6, maxHeight: 180, overflow: 'auto', fontSize: 10, lineHeight: 1.45, background: 'var(--g-bg)', padding: 8, borderRadius: 6, whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(m.rows, null, 1)}
                      </pre>
                    )}
                  </div>
                )}
                {/* G360_K852 - the offer. The guide's own opening line says what
                    the change does and does NOT do before anyone starts; the
                    title and that sentence both come from the guide JSON, never
                    from a model. */}
                {m.guide && !guide && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--g-divider)' }}>
                    <p style={{ margin: '0 0 6px', fontSize: 12.5, color: 'var(--g-text-2)' }}>{m.guide.opening}</p>
                    <button type="button" data-testid="guide-start" onClick={() => setGuide(m.guide)}
                      style={{ border: 0, background: 'var(--g-primary)', color: '#fff', borderRadius: 'var(--g-r-pill)',
                        padding: '6px 12px', fontSize: 12.5, cursor: 'pointer' }}>
                      Guide me through it
                    </button>
                  </div>
                )}
                {/* G360_K853 - the PLACE offer. Same shape as the task offer
                    above and the same rule: never started automatically, the
                    assistant routes and the human clicks. Every word here is
                    the surface's own label and sidebar group, read from the
                    generated registry - no model text reaches this card.
                    It deliberately promises only a location. Opening a page is
                    not doing the task, and saying more than that is how a
                    walkthrough starts lying about what it did. */}
                {m.place && !guide && !where && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--g-divider)' }}>
                    <p style={{ margin: '0 0 6px', fontSize: 12.5, color: 'var(--g-text-2)' }}>
                      {'That lives on ' + m.place.surface.label + ', under ' + m.place.surface.group + ' in the sidebar.'}
                    </p>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button type="button" data-testid="surface-start" onClick={() => setWhere({ surface: m.place.surface, question: m.place.question })}
                        style={{ border: 0, background: 'var(--g-primary)', color: '#fff', borderRadius: 'var(--g-r-pill)',
                          padding: '6px 12px', fontSize: 12.5, cursor: 'pointer' }}>
                        {'Show me ' + m.place.surface.label}
                      </button>
                      {/* A tie, not a ranked list. The matcher returns these only
                          when the scores are exactly equal, which for a real
                          question usually means the product genuinely splits the
                          job across two pages. Naming both teaches that split. */}
                      {(m.place.alternatives || []).map((alt) => (
                        <button key={alt.id} type="button" onClick={() => setWhere({ surface: alt, question: m.place.question })}
                          style={{ border: '1px solid var(--g-border)', background: 'var(--g-surface-2)',
                            color: 'inherit', borderRadius: 'var(--g-r-pill)', padding: '6px 12px', fontSize: 12.5, cursor: 'pointer' }}>
                          {'or ' + alt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {/* G360_K852 - the runner lives inside the panel so its outcome
                message and its live region sit in the conversation the user is
                already reading. The coach mark itself is painted on the page,
                not in here. */}
            {guide && <GuideRunner guide={guide} onEnd={() => setGuide(null)} />}
            {/* G360_K853 - navigation is handled by ConsoleApp's own navigate,
                passed in, so the Guide cannot reach a route the shell would not.
                Same portal as the runner: the card is painted on the page. */}
            {where && <SurfaceGuide surface={where.surface} question={where.question} ask={askAI} onNavigate={onNavigate} onEnd={() => setWhere(null)} />}
            {busy && <div style={{ alignSelf: 'flex-start', color: 'var(--g-text-2)', fontSize: 13 }}>Thinking...</div>}
            <div ref={endRef} />
          </div>
          <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid var(--g-divider)' }}>
            <input className="inp" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Ask a question..." />
            <button className="btn primary" onClick={() => send()} disabled={busy || !q.trim()} style={{ flexShrink: 0 }}><Send size={16} /></button>
          </div>
        </div>
      ), document.body)}
    </>
  );
}
